/**
 * PR2/F — página PÚBLICA del aviso de privacidad hospedado (/aviso/{slug}), sin
 * login. El tenant se resuelve por el slug en el backend (fn_public_aviso). Patrón
 * SelfInvoicePage/booking público: pathname + fetch crudo (sin Bearer).
 * V210 (hotfix) — el cuerpo es el DOCUMENTO PROPIO del tenant (.docx convertido a HTML
 * sanitizado, o PDF incrustado) con descarga del original. Nunca una plantilla, nunca el
 * recuadro "Plantilla base" (ese aviso es para el tenant, en su panel).
 */
import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { API_BASE } from '../lib/api';
import { AvisoDocumentView } from './AvisoDocumentView';
import { avisoVersionLabel, type AvisoDocumentKind } from './avisoDocument';

const PATH_RE = /^\/aviso\/([a-z0-9-]{1,100})\/?$/;

export const isAvisoRoute = (pathname: string): boolean => PATH_RE.test(pathname);

export interface PublicAviso {
  businessName: string;
  rfc: string | null;
  hostedAt: string | null;
  documentKind: AvisoDocumentKind;
  documentHtml: string | null;
  fileName: string;
  fileUploadedAt: string | null;
  fileShortHash: string;
}

async function publicJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { headers: { 'Content-Type': 'application/json' } });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) throw { status: res.status, body };
  return body as T;
}

/** Contenido del aviso publicado (puro: también lo pinta el test SSR). */
export function AvisoPublicBody({ aviso, fileUrl, pdfSrc }: { aviso: PublicAviso; fileUrl: string; pdfSrc: string | null }) {
  return (
    <>
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Aviso de privacidad</p>
      <h1 className="mt-1 font-serif text-2xl text-slate-900">{aviso.businessName}</h1>
      {aviso.rfc && <p className="mt-1 text-xs text-slate-500">RFC: <span className="font-mono">{aviso.rfc}</span></p>}
      <p className="mt-1 text-xs text-slate-400">
        {aviso.hostedAt ? `Publicado el ${new Date(aviso.hostedAt).toLocaleDateString('es-MX')} · ` : ''}
        {avisoVersionLabel(aviso.fileName, aviso.fileUploadedAt, aviso.fileShortHash)}
      </p>
      <div className="mt-6">
        <AvisoDocumentView kind={aviso.documentKind} html={aviso.documentHtml} pdfSrc={pdfSrc}
          fileName={aviso.fileName} downloadHref={fileUrl} />
      </div>
    </>
  );
}

export function AvisoPage() {
  const slug = window.location.pathname.match(PATH_RE)?.[1] ?? '';
  const [aviso, setAviso] = useState<PublicAviso | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [pdfSrc, setPdfSrc] = useState<string | null>(null);

  const pageUrl = `${window.location.origin}/aviso/${slug}`;
  const fileUrl = `${API_BASE}/public/aviso/${slug}/archivo`;

  useEffect(() => {
    publicJson<PublicAviso>(`/public/aviso/${slug}`)
      .then(setAviso)
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (aviso) QRCode.toDataURL(pageUrl, { margin: 1, width: 220 }).then(setQr).catch(() => {});
  }, [aviso, pageUrl]);

  // PDF: se incrusta desde un blob local (el archivo vive en el origen de la API).
  useEffect(() => {
    if (aviso?.documentKind !== 'pdf') return;
    let url: string | null = null;
    fetch(fileUrl).then((r) => (r.ok ? r.blob() : null)).then((b) => {
      if (b) { url = URL.createObjectURL(b); setPdfSrc(url); }
    }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [aviso, fileUrl]);

  const share = async () => {
    // Web Share nativo en móvil; fallback a copiar al portapapeles.
    if (navigator.share) {
      try { await navigator.share({ title: aviso?.businessName, url: pageUrl }); return; } catch { /* cancelado */ }
    }
    navigator.clipboard?.writeText(pageUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const shellCls = 'mx-auto w-full max-w-2xl rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-8';

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-10">
        <div className="mx-auto w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <p className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            Aviso no encontrado. Verifica el enlace o el código QR.
          </p>
        </div>
      </div>
    );
  }

  if (!aviso) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 text-sm text-slate-500">Cargando…</div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10">
      <div className={shellCls}>
        <AvisoPublicBody aviso={aviso} fileUrl={fileUrl} pdfSrc={pdfSrc} />

        {/* Compartir: link + QR para pegar en el establecimiento / ticket. */}
        <div className="mt-6 flex flex-col items-start gap-4 border-t border-slate-100 pt-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="w-full flex-1">
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">Comparte este aviso</p>
            <div className="mt-1 flex items-center gap-2">
              <input readOnly value={pageUrl} onFocus={(e) => e.target.select()}
                className="w-full min-w-0 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" />
              <button onClick={share}
                className="shrink-0 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50">
                {copied ? 'Copiado ✓' : 'Compartir'}
              </button>
            </div>
          </div>
          {qr && <img src={qr} alt="QR del aviso" className="h-28 w-28 rounded-lg border border-slate-100" />}
        </div>

        <p className="mt-6 text-center text-[11px] text-slate-400">
          Página hospedada por FluidSol.
        </p>
      </div>
    </div>
  );
}
